import { FC, FormEvent, useRef } from 'react';
import PhoneInputWithCountrySelect, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css'

type Props = {
  name: string;
  value: string;
  onChange: (phone: string | undefined) => void;
  placeholder?: string;
  error?: boolean;
  errorText?: string;
  country?: any;
  className?: string;
}

const Phone:FC<Props> = ({ name, value, onChange, placeholder = '', error = false, errorText = '', country, className = '' }) => {
  const rawInputRef = useRef('');

  const handleInput = (event: FormEvent<HTMLInputElement>) => {
    rawInputRef.current = event.currentTarget.value;
  };

  // Autofill (or paste) fills an empty field with a whole number at once. The "+" may be dropped
  // in national mode, and the number may come without "+" or a leading "0" (e.g. "380...").
  // Read it as international when that is valid, so the IP country doesn't hijack it.
  const handleChange = (phone: string | undefined) => {
    const raw = rawInputRef.current.trim();
    rawInputRef.current = '';
    if (!value && /^\+?[1-9][\d\s().-]*$/.test(raw)) {
      const candidate = `+${raw.replace(/\D/g, '')}`;
      if (candidate.length > 8 && isValidPhoneNumber(candidate)) {
        onChange(candidate);
        return;
      }
    }
    onChange(phone);
  };

  return (
    <div className="mb-4">
      <PhoneInputWithCountrySelect
        // Without a known country fall back to international input ("+" prefix),
        // otherwise parse national numbers (e.g. autofill) using the detected country
        international={!country}
        defaultCountry={country}
        name={name}
        value={value}
        onChange={handleChange}
        numberInputProps={{
          placeholder,
          autoComplete: 'tel',
          onInput: handleInput,
          style: {
            all: 'unset'
          }
        }}
        className={`
          outline-none
          w-full h-12
          bg-gray-200 rounded-2xl
          border ${error ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-primary'}
          shadow-neu-inner
          text-black
          text-base
          px-4 py-3
          placeholder:text-gray-400 appearance-none
          ${className}
        `}
      />
      {error && errorText ? (
        <span className="block text-red-500 text-xs pl-4 pt-2">{errorText}</span>
      ) : null}
    </div>
  )
}

export default Phone;